import { useState } from "react"
import { callAnalyzeAPI, checkSafety } from "../api/analyze"
import { shouldInterrupt } from "../utils/safety"
import { SoftWarning } from "./SoftWarning"

const CONTEXT_DRIFT_KEYWORDS_TR = ["diğer işler", "şunlar da var", "bunlar da", "başka şeyler", "diğer kararlar"]
const CONTEXT_DRIFT_KEYWORDS_EN = ["other things", "other tasks", "other decisions", "also need", "as well as", "everything else"]

interface Props {
  decision: string
  language: "tr" | "en"
  context: any
  otherDecisions: string[]
  onNext: (obstacles: string[], action: string, time: string, aiObstacle: string, aiAction: string, otherDecisions: string[]) => void
}

export const Step4Obstacles = ({ decision, language, context, otherDecisions, onNext }: Props) => {
  const [obstacleText, setObstacleText] = useState("")
  const [obstacles, setObstacles] = useState<string[]>([])
  const [obstacleAI, setObstacleAI] = useState<string>("")
  const [loadingObstacleAI, setLoadingObstacleAI] = useState(false)
  const [action, setAction] = useState("")
  const [time, setTime] = useState("")
  const [actionAI, setActionAI] = useState<string>("")
  const [loadingActionAI, setLoadingActionAI] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [showDriftWarning, setShowDriftWarning] = useState(false)

  const checkDrift = (text: string): boolean => {
    const keywords = language === "tr" ? CONTEXT_DRIFT_KEYWORDS_TR : CONTEXT_DRIFT_KEYWORDS_EN
    return keywords.some(k => text.toLowerCase().includes(k.toLowerCase()))
  }

  const handleAddObstacle = async () => {
    if (!obstacleText.trim()) return
    const newItem = obstacleText.trim()

    if (checkDrift(newItem)) {
      setShowDriftWarning(true)
      return
    }

    // Önce yerel keyword kontrolü
    if (shouldInterrupt(newItem, language)) {
      setShowWarning(true)
      return
    }

    // Sonra remote safety kontrolü — hata varsa fail-closed
    try {
      const safetyCheck = await checkSafety(newItem, language)
      if (!safetyCheck.safe) {
        setShowWarning(true)
        return
      }
    } catch {
      setShowWarning(true)
      return
    }

    // Tüm kontroller geçtiyse listeye ekle
    setObstacles(prev => [...prev, newItem])
    setObstacleText("")
    setShowWarning(false)
    setShowDriftWarning(false)
  }

  const handleRemoveObstacle = (index: number) => {
    setObstacles(obstacles.filter((_, i) => i !== index))
  }

  const handleGetObstacleAI = async () => {
    if (obstacles.length === 0) { setObstacleAI(" "); return }
    setLoadingObstacleAI(true)
    try {
      const analysis = await callAnalyzeAPI("OBSTACLE", obstacles.join(", "), language, { ...context, obstacles })
      setObstacleAI(analysis)
    } catch {
      alert(language === "tr" ? "Bir hata olustu." : "Something went wrong.")
    } finally {
      setLoadingObstacleAI(false)
    }
  }

  const handleGetActionAI = async () => {
    if (!action || !time) {
      alert(language === "tr" ? "Aksiyon ve saati gir." : "Please fill in both fields.")
      return
    }
    setLoadingActionAI(true)
    try {
      const support = await callAnalyzeAPI("DECISION", `${action} at ${time}`, language, context)
      const crisisKeywords = ["profesyonel destek", "182", "professional help", "988"]
      if (crisisKeywords.some(k => support.toLowerCase().includes(k))) setShowWarning(true)
      setActionAI(support)
    } catch {
      alert(language === "tr" ? "Bir hata olustu." : "Something went wrong.")
    } finally {
      setLoadingActionAI(false)
    }
  }

  const t = language === "tr" ? {
    stepLabel: "Adım 4",
    focusLabel: "ODAK",
    headline: "Bu kararı uygulamanı ne engelliyor?",
    subtext: "Sadece bu karar için yaz. Diğer konuları değil.",
    obstaclePlaceholder: "Örn: Başlamak için mükemmel olmasını bekliyorum",
    addBtn: "Engeli ekle",
    obstaclesLabel: "Engeller",
    continueBtn: "Devam Et",
    analyzingBtn: "Şekilleniyor...",
    actionQ: "İlk adımın ne?",
    actionPlaceholder: "İlk olarak ne yapacaksın?",
    timeLabel: "Saat?",
    supportBtn: "Devam Et",
    processingBtn: "Netleşiyor...",
    completeBtn: "Tamamla",
    driftWarning: "Bu farklı bir konu gibi görünüyor. Sadece seçtiğin kararın önündeki engeli yaz.",
  } : {
    stepLabel: "Step 4",
    focusLabel: "FOCUS",
    headline: "What is stopping you from acting on THIS decision?",
    subtext: "Write only about this decision. Ignore others.",
    obstaclePlaceholder: "E.g. I'm waiting for it to be perfect before starting",
    addBtn: "Add obstacle",
    obstaclesLabel: "Obstacles",
    continueBtn: "Continue",
    analyzingBtn: "Shaping...",
    actionQ: "First move?",
    actionPlaceholder: "What will you do first?",
    timeLabel: "When?",
    supportBtn: "Continue",
    processingBtn: "Clarifying...",
    completeBtn: "Complete",
    driftWarning: "This looks like a different topic. Focus only on what's blocking this decision.",
  }

  return (
    <div>
      <div className="step-label">{t.stepLabel}</div>

      {/* FOCUS CARD */}
      <div style={{
        padding: "14px 18px",
        background: "rgba(123,92,255,0.08)",
        border: "1px solid rgba(123,92,255,0.25)",
        borderRadius: "12px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "10px", fontWeight: "600", letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent-2)", marginBottom: "6px" }}>
          {t.focusLabel}
        </div>
        <div style={{ fontSize: "17px", fontWeight: "600", color: "var(--text-primary)", lineHeight: "1.4" }}>
          {decision}
        </div>
      </div>

      {!obstacleAI && (
        <div>
          <div style={{ fontSize: "18px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "6px", lineHeight: "1.4" }}>
            {t.headline}
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "16px" }}>
            {t.subtext}
          </div>

          <textarea
            value={obstacleText}
            onChange={(e) => { setObstacleText(e.target.value); setShowDriftWarning(false) }}
            onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddObstacle() } }}
            placeholder={t.obstaclePlaceholder}
            rows={2}
          />

          {showDriftWarning && (
            <div style={{
              padding: "10px 14px",
              background: "rgba(224,112,104,0.07)",
              border: "1px solid rgba(224,112,104,0.2)",
              borderRadius: "10px",
              marginTop: "8px",
              fontSize: "13px",
              color: "var(--red)",
              lineHeight: "1.5",
            }}>
              {t.driftWarning}
            </div>
          )}

          <div className="button-row">
            <button onClick={handleAddObstacle} className="btn-add">
              {t.addBtn}
            </button>
            <button
              onClick={handleGetObstacleAI}
              disabled={loadingObstacleAI}
              className={`btn-primary${loadingObstacleAI ? " btn-loading" : ""}`}
            >
              {loadingObstacleAI ? t.analyzingBtn : t.continueBtn}
            </button>
          </div>

          {showWarning && <SoftWarning language={language} onDismiss={() => setShowWarning(false)} />}

          {obstacles.length > 0 && (
            <div className="obstacles-list">
              <div className="section-label">{t.obstaclesLabel}</div>
              {obstacles.map((obs, i) => (
                <div key={i} className="list-item">
                  <div className="list-item-bullet" />
                  <span className="list-item-text">{obs}</span>
                  <button className="remove-btn" onClick={() => handleRemoveObstacle(i)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {obstacleAI && !actionAI && (
        <div>
          {obstacleAI.trim() && (
            <div className="ai-box">
              <p>{obstacleAI}</p>
            </div>
          )}

          <div className="question-text">{t.actionQ}</div>

          <textarea
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder={t.actionPlaceholder}
            rows={3}
          />

          <div className="time-input-wrapper">
            <span className="field-label">{t.timeLabel}</span>
            <input
              className="time-input"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          {action && time && (
            <button
              onClick={handleGetActionAI}
              disabled={loadingActionAI}
              className={`btn-primary btn-mt${loadingActionAI ? " btn-loading" : ""}`}
            >
              {loadingActionAI ? t.processingBtn : t.supportBtn}
            </button>
          )}
        </div>
      )}

      {actionAI && (
        <div>
          <div className="ai-box">
            <p>{actionAI}</p>
          </div>
          {showWarning && <SoftWarning language={language} onDismiss={() => setShowWarning(false)} />}
          <button
            onClick={() => onNext(obstacles, action, time, obstacleAI, actionAI, otherDecisions)}
            className="btn-primary"
          >
            {t.completeBtn}
          </button>
        </div>
      )}
    </div>
  )
}